import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { formatSgd, formatNum, apiErr } from "../../lib/utils";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input, Label, Textarea } from "../ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../ui/table";
import { toast } from "sonner";
import { ArrowLeft, Activity, Plus, Trash2, CalendarClock } from "lucide-react";
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { STAGE_TONE } from "./InvestmentRegister";

export const MRV_TONE = {
  tracking: "text-[#00E65B] border-[#00E65B]/40",
  overdue: "text-red-400 border-red-400/40",
  awaiting: "text-[#FFB800] border-[#FFB800]/40",
  planned: "text-[#00B4E6] border-[#00B4E6]/40",
  not_planned: "text-muted-foreground border-border",
};
const RAG_TEXT = { green: "text-[#00E65B]", amber: "text-[#FFB800]", red: "text-red-400" };
const PERIOD_HINT = { monthly: "2026-06", quarterly: "2026-Q2", annual: "2026" };

function AchBadge({ pct, rag }) {
  if (pct == null) return <span className="text-xs text-muted-foreground">—</span>;
  return <span className={`font-mono text-sm font-bold ${RAG_TEXT[rag] || ""}`}>{pct}%</span>;
}

function PlanCard({ iid, plan, onSaved }) {
  const [f, setF] = useState(plan);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put(`/susfin/mrv/${iid}/plan`, {
        measurement_method: f.measurement_method || null, data_source: f.data_source || null,
        frequency: f.frequency || null, verifier: f.verifier || null, responsible: f.responsible || null,
      });
      onSaved(r.data); toast.success("MRV plan saved");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="card-flat p-4 space-y-3" data-testid="sf-mrv-plan">
      <div className="label-eyebrow text-[10px]">MRV Plan</div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div><Label className="text-[10px]">Measurement method</Label>
          <Input value={f.measurement_method || ""} onChange={(e) => set("measurement_method", e.target.value)} placeholder="Sub-metered kWh vs baseline" data-testid="sf-mrv-plan-method" /></div>
        <div><Label className="text-[10px]">Data source</Label>
          <Input value={f.data_source || ""} onChange={(e) => set("data_source", e.target.value)} placeholder="Utility bills / BMS export" data-testid="sf-mrv-plan-source" /></div>
        <div><Label className="text-[10px]">Frequency</Label>
          <Select value={f.frequency || "none"} onValueChange={(v) => set("frequency", v === "none" ? "" : v)}>
            <SelectTrigger data-testid="sf-mrv-plan-frequency"><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">—</SelectItem>
              <SelectItem value="monthly">Monthly</SelectItem>
              <SelectItem value="quarterly">Quarterly</SelectItem>
              <SelectItem value="annual">Annual</SelectItem>
            </SelectContent>
          </Select></div>
        <div><Label className="text-[10px]">Verifier</Label>
          <Input value={f.verifier || ""} onChange={(e) => set("verifier", e.target.value)} placeholder="Internal / external assurer" /></div>
        <div><Label className="text-[10px]">Responsible owner</Label>
          <Input value={f.responsible || ""} onChange={(e) => set("responsible", e.target.value)} placeholder="Facilities manager" data-testid="sf-mrv-plan-responsible" /></div>
      </div>
      <Button size="sm" variant="primary" onClick={save} disabled={busy} data-testid="sf-mrv-plan-save">{busy ? "Saving…" : "Save plan"}</Button>
    </div>
  );
}

function ReadingForm({ iid, d, onSaved }) {
  const tracked = d.metrics.filter((m) => m.tracked);
  const [period, setPeriod] = useState("");
  const [vals, setVals] = useState({});
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const freq = d.plan.frequency;
  const submit = async () => {
    setBusy(true);
    try {
      const values = {};
      Object.entries(vals).forEach(([k, v]) => { if (v !== "" && v != null) values[k] = Number(v); });
      const r = await api.post(`/susfin/mrv/${iid}/readings`, { period, values, note: note || null });
      onSaved(r.data); setPeriod(""); setVals({}); setNote("");
      toast.success("Actual reading recorded");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  if (!freq) return (
    <div className="card-flat p-4 text-sm text-muted-foreground" data-testid="sf-mrv-no-freq">
      Set the measurement frequency in the MRV plan to start recording actuals.
    </div>
  );
  if (tracked.length === 0) return (
    <div className="card-flat p-4 text-sm text-muted-foreground" data-testid="sf-mrv-no-expected">
      No expected values recorded — add expected impact (tCO₂e, savings, energy) on the investment first.
    </div>
  );
  return (
    <div className="card-flat p-4 space-y-3" data-testid="sf-mrv-reading-form">
      <div className="label-eyebrow text-[10px]">Record actual reading ({freq})</div>
      <div className="grid sm:grid-cols-3 gap-3">
        <div><Label className="text-[10px]">Period</Label>
          <Input value={period} onChange={(e) => setPeriod(e.target.value)} placeholder={PERIOD_HINT[freq]} data-testid="sf-mrv-reading-period" /></div>
        {tracked.map((m) => (
          <div key={m.key}><Label className="text-[10px]">{m.label} ({m.unit})</Label>
            <Input type="number" value={vals[m.key] ?? ""} onChange={(e) => setVals((p) => ({ ...p, [m.key]: e.target.value }))}
              placeholder={m.expected_per_period != null ? `expected ${m.expected_per_period}` : ""} data-testid={`sf-mrv-reading-${m.key}`} /></div>
        ))}
      </div>
      <Textarea rows={1} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Evidence note (e.g. March utility bill ref)" data-testid="sf-mrv-reading-note" />
      <Button size="sm" variant="primary" onClick={submit} disabled={busy || !period} data-testid="sf-mrv-reading-add">
        <Plus className="h-3.5 w-3.5" /> {busy ? "Recording…" : "Add reading"}
      </Button>
    </div>
  );
}

function TrendChart({ d }) {
  const primary = d.metrics.find((m) => m.tracked && d.readings.some((r) => r.entries.some((e) => e.key === m.key)));
  if (!primary || d.readings.length === 0) return null;
  const rows = d.readings.map((r) => {
    const e = r.entries.find((x) => x.key === primary.key);
    return { period: r.period, actual: e ? e.actual : null, expected: primary.expected_per_period };
  });
  return (
    <div className="card-flat p-4" data-testid="sf-mrv-chart">
      <div className="label-eyebrow text-[10px] mb-2">Expected vs actual — {primary.label} ({primary.unit})</div>
      <ResponsiveContainer width="100%" height={200}>
        <ComposedChart data={rows} margin={{ top: 4, right: 8, bottom: 0, left: -12 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
          <XAxis dataKey="period" tick={{ fontSize: 10, fill: "#888" }} />
          <YAxis tick={{ fontSize: 10, fill: "#888" }} />
          <Tooltip contentStyle={{ background: "#15161a", border: "1px solid #333", fontSize: 12 }} />
          <Bar dataKey="actual" name="Actual" fill="#D4FF00" fillOpacity={0.75} radius={[2, 2, 0, 0]} maxBarSize={36} />
          <Line dataKey="expected" name="Expected" stroke="#FFB800" strokeDasharray="5 4" dot={false} strokeWidth={1.5} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

function MrvWorkspace({ iid, onBack }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    setD(null);
    api.get(`/susfin/mrv/${iid}`).then((r) => setD(r.data)).catch((e) => toast.error(apiErr(e)));
  }, [iid]);
  if (!d) return <div className="p-8 text-muted-foreground font-mono text-sm">Loading MRV workspace…</div>;

  const s = d.summary;
  const delReading = async (rid) => {
    try {
      const r = await api.delete(`/susfin/mrv/${iid}/readings/${rid}`);
      setD(r.data); toast.success("Reading removed");
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-4" data-testid="sf-mrv-workspace">
      <div>
        <button onClick={onBack} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-1" data-testid="sf-mrv-back">
          <ArrowLeft className="h-4 w-4" /> MRV portfolio
        </button>
        <h2 className="font-display text-2xl font-bold tracking-tight flex items-center gap-2 flex-wrap">
          {d.initiative_name}
          <Badge variant="outline" className={`text-[9px] uppercase ${MRV_TONE[s.status]}`} data-testid="sf-mrv-status">{s.status_label}</Badge>
        </h2>
        <p className="text-xs text-muted-foreground mt-0.5">
          {s.readings_count} reading{s.readings_count !== 1 ? "s" : ""} recorded
          {s.next_due ? ` · next due ${s.next_due}` : ""}
        </p>
      </div>

      {s.per_metric.length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="card-flat p-4" data-testid="sf-mrv-overall">
            <div className="label-eyebrow text-[10px] mb-1">Overall achievement</div>
            <div className={`font-display text-3xl font-bold ${RAG_TEXT[s.rag] || ""}`}>{s.achievement_pct != null ? `${s.achievement_pct}%` : "—"}</div>
            <div className="text-[10px] text-muted-foreground mt-0.5">of expected outcome to date</div>
          </div>
          {s.per_metric.map((m) => (
            <div key={m.key} className="card-flat p-4" data-testid={`sf-mrv-metric-${m.key}`}>
              <div className="label-eyebrow text-[10px] mb-1">{m.label}</div>
              <div className="font-mono text-lg font-bold">
                {m.key === "savings_sgd" ? formatSgd(m.actual_to_date) : `${formatNum(m.actual_to_date, { decimals: 1 })} ${m.unit}`}
              </div>
              <div className="text-[10px] text-muted-foreground mt-0.5">
                vs {m.key === "savings_sgd" ? formatSgd(m.expected_to_date) : formatNum(m.expected_to_date, { decimals: 1 })} expected · <span className={RAG_TEXT[m.achievement_pct >= 90 ? "green" : m.achievement_pct >= 70 ? "amber" : "red"]}>{m.achievement_pct}%</span>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        <PlanCard iid={iid} plan={d.plan} onSaved={setD} />
        <ReadingForm iid={iid} d={d} onSaved={setD} />
      </div>

      <TrendChart d={d} />

      <div className="card-flat overflow-x-auto" data-testid="sf-mrv-readings">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Period</TableHead><TableHead>Actuals</TableHead><TableHead>Variance vs expected</TableHead>
            <TableHead>Note</TableHead><TableHead>By</TableHead><TableHead />
          </TableRow></TableHeader>
          <TableBody>
            {[...d.readings].reverse().map((r) => (
              <TableRow key={r.id} data-testid={`sf-mrv-reading-row-${r.id}`}>
                <TableCell className="font-mono text-xs font-bold">{r.period}</TableCell>
                <TableCell className="text-xs">
                  {r.entries.map((e) => (
                    <div key={e.key}><span className="text-muted-foreground">{e.label}:</span> <span className="font-mono">{e.key === "savings_sgd" ? formatSgd(e.actual) : `${formatNum(e.actual, { decimals: 1 })} ${e.unit}`}</span></div>
                  ))}
                </TableCell>
                <TableCell className="text-xs">
                  {r.entries.map((e) => (
                    <div key={e.key}>
                      {e.achievement_pct != null ? (
                        <span className={`font-mono ${RAG_TEXT[e.achievement_pct >= 90 ? "green" : e.achievement_pct >= 70 ? "amber" : "red"]}`}>
                          {e.variance > 0 ? "+" : ""}{formatNum(e.variance, { decimals: 1 })} ({e.achievement_pct}%)
                        </span>
                      ) : <span className="text-muted-foreground">—</span>}
                    </div>
                  ))}
                </TableCell>
                <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate">{r.note || "—"}</TableCell>
                <TableCell className="text-[10px] text-muted-foreground">{r.recorded_by}</TableCell>
                <TableCell className="text-right">
                  <button onClick={() => delReading(r.id)} className="text-muted-foreground hover:text-red-400 p-1" data-testid={`sf-mrv-reading-del-${r.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {d.readings.length === 0 && (
          <div className="p-8 text-center text-muted-foreground text-sm" data-testid="sf-mrv-readings-empty">
            <Activity className="h-6 w-6 mx-auto mb-2 opacity-50" /> No actual readings recorded yet.
          </div>
        )}
      </div>
      <p className="text-[10px] text-muted-foreground">{d.disclaimer}</p>
    </div>
  );
}

export default function MrvTab({ openId, clearOpenId }) {
  const [d, setD] = useState(null);
  const [active, setActive] = useState(null);
  const load = () => api.get("/susfin/mrv-portfolio").then((r) => setD(r.data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); }, []);
  useEffect(() => { if (openId) { setActive(openId); clearOpenId?.(); } }, [openId]);

  if (active) return <MrvWorkspace iid={active} onBack={() => { setActive(null); load(); }} />;
  if (!d) return <div className="p-8 text-muted-foreground font-mono text-sm">Loading MRV portfolio…</div>;

  const r = d.rollup || {};
  return (
    <div className="space-y-4" data-testid="sf-mrv-tab">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="card-flat p-4" data-testid="sf-mrv-roll-tracking">
          <div className="label-eyebrow text-[10px] mb-1">Tracking</div>
          <div className="font-display text-3xl font-bold">{r.with_readings || 0}</div>
          <div className="text-[10px] text-muted-foreground mt-0.5">{(r.counts?.overdue || 0)} overdue · {(r.counts?.awaiting || 0)} awaiting first reading</div>
        </div>
        <div className="card-flat p-4" data-testid="sf-mrv-roll-achievement">
          <div className="label-eyebrow text-[10px] mb-1">Avg achievement</div>
          <div className={`font-display text-3xl font-bold ${r.avg_achievement_pct != null ? RAG_TEXT[r.avg_achievement_pct >= 90 ? "green" : r.avg_achievement_pct >= 70 ? "amber" : "red"] : ""}`}>
            {r.avg_achievement_pct != null ? `${r.avg_achievement_pct}%` : "—"}
          </div>
          <div className="text-[10px] text-muted-foreground mt-0.5">actual vs expected outcomes</div>
        </div>
        <div className="card-flat p-4" data-testid="sf-mrv-roll-emissions">
          <div className="label-eyebrow text-[10px] mb-1">Emissions (annualised)</div>
          <div className="font-mono text-lg font-bold">{formatNum(r.actual_emissions_t || 0, { decimals: 1 })} t</div>
          <div className="text-[10px] text-muted-foreground mt-0.5">vs {formatNum(r.expected_emissions_t || 0, { decimals: 1 })} t expected</div>
        </div>
        <div className="card-flat p-4" data-testid="sf-mrv-roll-savings">
          <div className="label-eyebrow text-[10px] mb-1">Savings (annualised)</div>
          <div className="font-mono text-lg font-bold">{formatSgd(r.actual_savings_sgd || 0)}</div>
          <div className="text-[10px] text-muted-foreground mt-0.5">vs {formatSgd(r.expected_savings_sgd || 0)} expected</div>
        </div>
      </div>

      <div className="space-y-2">
        {(d.items || []).map((i) => (
          <button key={i.id} onClick={() => setActive(i.id)} data-testid={`sf-mrv-row-${i.id}`}
            className="w-full text-left card-flat p-4 hover:bg-surface-hover transition-colors flex items-center gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-display font-semibold truncate">{i.name}</span>
                <Badge variant="outline" className={`text-[9px] uppercase ${STAGE_TONE[i.stage] || ""}`}>{i.stage.replace(/_/g, " ")}</Badge>
                <Badge variant="outline" className={`text-[9px] uppercase ${MRV_TONE[i.status]}`}>{i.status_label}</Badge>
              </div>
              <div className="text-xs text-muted-foreground mt-0.5 truncate">
                Expected {formatNum(i.expected_emissions_t, { decimals: 1 })} tCO₂e/yr · {formatSgd(i.expected_savings_sgd)}/yr
                {i.next_due ? <span className="inline-flex items-center gap-1 ml-2"><CalendarClock className="h-3 w-3" /> next {i.next_due}</span> : null}
              </div>
            </div>
            <div className="text-right shrink-0">
              <AchBadge pct={i.achievement_pct} rag={i.rag} />
              <div className="text-[10px] text-muted-foreground">{i.readings} reading{i.readings !== 1 ? "s" : ""}</div>
            </div>
          </button>
        ))}
        {(d.items || []).length === 0 && (
          <div className="card-flat p-12 text-center text-muted-foreground" data-testid="sf-mrv-empty">
            <Activity className="h-8 w-8 mx-auto mb-3 opacity-50" />
            No investments with expected impact or an MRV plan yet — quantify expected benefits on register investments first.
          </div>
        )}
      </div>
    </div>
  );
}
